import { isFunction, isPlainObject } from 'lodash';
import { Toast } from 'vant';
import { detectEnv } from './detect';
import {
  FaceLocations,
  NotifyPaymentProductTypes,
  NotifyPaymentStatus,
  WeChatPaymentStatus,
  AlipayPaymentStatus,
  YS_WECHAT_MP_ID,
  WECHAT_MINI_PROGRAM_TYPE,
  WeChatMiniProgramTypes,
  NotifyPaymentText,
  PaymentMerchant,
  ZB_WECHAT_MP_ID,
  RemindersRewriteTypes,
  OriginPageTypes,
} from '@/shared/constants';
import { FundProviders } from '@/shared/funds';
import { useUserStore } from '@/stores/modules/user';
import { type AddAmountListItem } from '@/services/creditLimit';
import { stringifyQuery } from './query';

interface BridgeRequest {
  action: string;
  params?: Record<string, any>;
}

type Callback = (err: Error | null, response: BridgeResponse) => void;
type Watcher = {
  callback: Callback;
  id: string;
};

const callbacks = new Map<
  string,
  {
    callback?: Callback;
    watchers?: Watcher[];
    isWatcherMode: boolean;
  }
>();
let seed = 1;

const enum BridgeCode {
  FAIL = '-2',
  ACTION_NOT_FOUND = '-1',
  SUCCESS = '1',
}

const MessageMap = {
  [BridgeCode.FAIL]: 'The action call failed.',
  [BridgeCode.ACTION_NOT_FOUND]: 'Please download the lastest version of the app.',
  [BridgeCode.SUCCESS]: 'The action call success.',
};

const originalReceiver = window.yxApp_resApp;

/**
 * 处理JSBridge回调
 */
function receive(this: unknown, res: Omit<BridgeResponse, 'message'> & { _action: string }) {
  // 测试环境和开发环境增加调试信息
  if (process.env.VUE_APP_RUNTIME_ENV !== 'prod' && process.env.VUE_APP_RUNTIME_ENV !== 'staging') {
    console.info(`jsbridge response: ${JSON.stringify(res, null, '  ')}`);
  }

  if (isFunction(originalReceiver)) {
    originalReceiver.call(this, res);
  }

  const execute = (fn?: Callback) => {
    if (!isFunction(fn)) return;

    let bridgeCode = String(res.code) as BridgeCode;

    /**
     * 除了以下状态，其他code统一设置为失败
     * 1. 成功状态
     * 2. 方法不存在(通常为APP版本太旧)
     */
    if (bridgeCode !== BridgeCode.SUCCESS && bridgeCode !== BridgeCode.ACTION_NOT_FOUND) {
      bridgeCode = BridgeCode.FAIL;
    }

    let error = null;

    if (bridgeCode !== BridgeCode.SUCCESS) {
      error = new Error(MessageMap[bridgeCode]);
      error.name = 'BizError';
    }

    fn(error, {
      code: bridgeCode,
      data: res.data,
      message: MessageMap[bridgeCode],
    });
  };

  const { callback, watchers, isWatcherMode } = callbacks.get(res._action) ?? {};
  // watcher模式调用完回调不删除，由用户自己决定删除时机
  if (isWatcherMode) {
    if (watchers) {
      // 始终执行最后注册的watcher，其被移除后前一个watcher自动恢复接收
      execute(watchers[watchers.length - 1].callback);
    }
  } else {
    execute(callback);
    callbacks.delete(res._action);
  }
}

// 在宜享花APP中注册回调参数
if (detectEnv.yxh) {
  window.yxApp_resApp = receive;
}

type WatchStopHandle = (fn?: (finised: boolean) => void) => void;

/**
 * 调用JSBridge
 * @param {Object} data
 * @param {Object} data.action 需要调用的方法名
 * @param {Object} data.params 方法的参数
 * @param {Function} callback 回调函数
 * @param {Boolean} isWatcherMode 当指定为true时，传入的回调函数执行完成后不会自动释放，同时invoke将返回用于手动释放的函数
 */
function invoke(
  data: BridgeRequest,
  callback?: Callback,
  isWatcherMode = false,
): WatchStopHandle | void {
  // 如果不在宜享花APP中则提示用户
  if (!detectEnv.yxh) {
    Toast.fail('请在宜享花APP中操作');
    return;
  }

  const finalData: BridgeRequest & { _action?: string } = {
    ...data,
  };

  let unwatch: WatchStopHandle | undefined;
  let callbackName: string;

  // watcher模式下使用action作为回调名称，同时将callback以数组形式存储
  if (isWatcherMode) {
    callbackName = data.action;
    const watcherId = `watcher_${Date.now()}_${seed++}`;

    if (isFunction(callback)) {
      let watchers: Watcher[] = [];
      const prevWatchers = callbacks.get(callbackName)?.watchers;
      const watcher = {
        callback,
        id: watcherId,
      };

      if (prevWatchers) {
        watchers = [...prevWatchers, watcher];
      } else {
        watchers = [watcher];
      }

      callbacks.set(callbackName, {
        watchers,
        isWatcherMode,
      });
    }

    unwatch = (fn) => {
      const watchers = callbacks.get(callbackName)?.watchers;

      if (Array.isArray(watchers) && watchers.length > 1) {
        // 根据id找到对应的watcher进行删除
        const index = watchers.findIndex((watcher) => watcher.id === watcherId);
        if (index >= 0) {
          watchers.splice(index, 1);
        }

        fn?.(false);
      } else {
        callbacks.delete(callbackName);
        fn?.(true);
      }
    };
  } else {
    callbackName = `callback_${Date.now()}_${seed++}`;

    if (isFunction(callback)) {
      callbacks.set(callbackName, {
        callback,
        isWatcherMode,
      });
    }
  }

  finalData._action = callbackName;

  // 分享给个默认参数
  if (data.action === 'share' && data.params?.shareType == null) {
    finalData.params = {
      ...finalData.params,
      shareType: ['wxFriend', 'wxZone'],
    };
  }

  // 测试环境和开发环境增加调试信息
  if (process.env.VUE_APP_RUNTIME_ENV !== 'prod' && process.env.VUE_APP_RUNTIME_ENV !== 'staging') {
    console.info(`jsbridge request: ${JSON.stringify(finalData, null, '  ')}`);
  }

  if (detectEnv.ios) {
    try {
      window.webkit.messageHandlers.yxApp_iOS.postMessage(JSON.stringify(finalData));
    } catch (err) {
      // 测试环境和开发环境增加调试信息
      if (
        process.env.VUE_APP_RUNTIME_ENV !== 'prod' &&
        process.env.VUE_APP_RUNTIME_ENV !== 'staging'
      ) {
        console.error(`jsbridge error: ${err}`);
      }

      if (isFunction(callback)) {
        const message = MessageMap[BridgeCode.FAIL];

        if (!isWatcherMode) {
          callbacks.delete(callbackName);
        }

        callback(new Error(message), {
          code: BridgeCode.FAIL,
          message,
        });

        Toast.fail(message);
      }
    }
  }

  if (detectEnv.android || detectEnv.harmony) {
    try {
      window.yxApp_bridge.reqApp(JSON.stringify(finalData));
    } catch (err) {
      // 测试环境和开发环境增加调试信息
      if (
        process.env.VUE_APP_RUNTIME_ENV !== 'prod' &&
        process.env.VUE_APP_RUNTIME_ENV !== 'staging'
      ) {
        console.error(`jsbridge error: ${err}`);
      }

      if (isFunction(callback)) {
        const message = MessageMap[BridgeCode.FAIL];

        if (!isWatcherMode) {
          callbacks.delete(callbackName);
        }

        callback(new Error(message), {
          code: BridgeCode.FAIL,
          message,
        });

        Toast.fail(message);
      }
    }
  }

  return unwatch;
}

interface UserInfo {
  mobile?: string;
  token: string;
  userId?: number;
}

/**
 * 获取用户信息
 */
function getUserInfo() {
  return new Promise<UserInfo>((resolve, reject) => {
    invoke(
      {
        action: 'getUserInfo',
      },
      (err, response: BridgeResponse<UserInfo>) => {
        if (err) {
          return reject(err);
        }

        const { data } = response;
        const token = data?.token;
        const userStore = useUserStore();

        if (data && token) {
          try {
            userStore.syncLoginByApp({
              token,
              phone: data.mobile,
              userId: data.userId,
            });
          } catch {}
          resolve(data);
        } else {
          try {
            userStore.syncLoginByApp({
              token: null,
              phone: null,
              userId: null,
            });
          } catch {}
          reject(new Error('无效的Token'));
        }
      },
    );
  });
}

/**
 * 启动APP登录
 */
function launchLogin(originToken = '') {
  return new Promise<UserInfo>((resolve, reject) => {
    invoke(
      {
        action: 'openLogin',
        params: {
          token: originToken,
        },
      },
      (err, response: BridgeResponse<UserInfo>) => {
        if (err) {
          return reject(err);
        }

        const { data } = response;
        const token = data?.token;
        const userStore = useUserStore();

        if (data && token) {
          try {
            // 登录成功后将token和手机号写入到Store中

            userStore.syncLoginByApp({
              token,
              phone: data.mobile,
            });
          } catch {}
          resolve(data);
        } else {
          reject(new Error('无效的Token'));
        }
      },
    );
  });
}

export interface FaceRecognitionParams {
  // 用户姓名
  name?: string;
  // 用户身份证
  idNo?: string;
  // 1 => 表示商城分期流程唤起
  loanType?: '1';
  // 人脸位置
  faceLocation?: FaceLocations;
  // 入口标识
  entryFlag?: string;
  // 登录token
  token?: string | null;
  // 公告文本
  noticeText?: string;
  // 人脸识别提供方
  faceProvider?: string;
  // 是否需要H5人脸CA
  h5Flag?: number;
  // 人脸结果页成功后回调页面
  returnUrl?: string;
}

interface FaceRecognitionResult {
  status: 1 | 2 | 3;
}

/**
 * 启动人脸识别
 */
function launchFaceRecognition(params: FaceRecognitionParams) {
  return new Promise<FaceRecognitionResult>((resolve, reject) => {
    invoke(
      {
        action: 'launchFaceRecognition',
        params,
      },
      (err, response: BridgeResponse<FaceRecognitionResult>) => {
        if (err) {
          return reject(err);
        }

        const { data } = response;

        if (data) {
          resolve(data);
        } else {
          reject(new Error('获取人脸数据失败'));
        }
      },
    );
  });
}

/**
 * 检查APP登录状态
 * 未登录将自动唤起APP登录，如果已登录将返回登录后的信息
 */
async function checkLogin() {
  try {
    return await getUserInfo();
  } catch {
    return launchLogin();
  }
}

interface OpenWebViewWithAutofillPhoneNumberParams {
  // 要打开的产品名称
  productName?: string;
  // 要打开的产品名称
  productId?: number;
  // 要打开的产品地址
  productUrl: string;
  // 要携带的js代码
  js?: string;
  // 是否注入劫持网络请求脚本
  getRegisterInfoFlag?: boolean;
}

/**
 * 打开新的WebView并自动填充手机号
 */
function openWebViewWithAutofillPhoneNumber(params: OpenWebViewWithAutofillPhoneNumberParams) {
  invoke({
    action: 'openWebViewAutoBringPhone',
    params,
  });
}

interface OpenWebViewParams {
  // 要打开的地址
  url: string;
  // 是否显示标题栏
  showTitle?: boolean;
  // 打开WebView时请求URL方法
  method?: 'get' | 'post';
  // 要携带的参数
  params?: Record<string, string | null>;
  // 关闭WebView时是否退到根视图，默认false
  whenCloseToRoot?: boolean;
}

/**
 * 打开新的WebView
 */
function openWebView(params: OpenWebViewParams) {
  const { url, showTitle = true, whenCloseToRoot = false, method = 'get' } = params;

  invoke({
    action: 'openWebView',
    params: {
      url,
      method,
      params: params.params ? stringifyQuery(params.params) : '',
      showTitle,
      whenCloseToRoot,
    },
  });
}

interface CheckAgreeAutofillPhoneNumberResult {
  isAgree: 1 | 0;
}

/**
 * 检查是否同意自动填充手机号
 */
function checkAgreeAutofillPhoneNumber() {
  return new Promise<CheckAgreeAutofillPhoneNumberResult>((resolve, reject) => {
    invoke(
      {
        action: 'daoliuConfirmDialogAgree',
      },
      (err, response: BridgeResponse<CheckAgreeAutofillPhoneNumberResult>) => {
        if (err) {
          return reject(err);
        }
        const { data } = response;
        if (data) {
          resolve(data);
        } else {
          reject(new Error('查询导流弹窗失败'));
        }
      },
    );
  });
}

/**
 * 保存同意自动填充手机号
 */
function saveAgreeAutofillPhoneNumber() {
  invoke({
    action: 'saveDaoliuConfirmDialogAgreed',
  });
}

/**
 * 关闭当前Webview窗口
 */
function close() {
  invoke({
    action: 'closeCurrentWebView',
  });
}

/**
 * 后退history，当没有页面可退时将关闭webview
 */
function back() {
  invoke({
    action: 'backOrCloseWebView',
  });
}

export type ShareType = 'wxFriend' | 'wxZone' | 'image_wxFriend' | 'image_wxZone';

interface ShareParams {
  // 分享标题
  title: string;
  // 分享内容
  content: string;
  // 分享后打开的链接，需要encodeURI/encodeURIComponent
  url: string;
  // 分享的图标地址，为空或下载失败将使用默认图像
  picUrl?: string;
  // 分享的图标base64数据，为空或下载失败将使用默认图像
  picData?: string;
  // 当shareType为image_wxFriend、image_wxZone时使用的图片URL地址
  picUrl4Image?: string;
  // 当shareType为image_wxFriend、image_wxZone时使用的图片base64数据
  picData4Image?: string;
  // wxFriend(微信好友)、 wxZone(微信朋友圈)、image_wxFriend、image_wxZone。默认值: ['wxFriend', 'wxZone']。如果需要显示全部渠道请传空数组，即 []
  shareType: ShareType[];
}

/**
 * 分享
 **/
function share(params: ShareParams) {
  return new Promise<void>((resolve, reject) => {
    invoke(
      {
        action: 'share',
        params,
      },
      (err, response: BridgeResponse<{ status: string }>) => {
        if (err) {
          return reject(err);
        }

        const { data } = response;

        if (data && data.status === 'success') {
          resolve();
        } else {
          reject(new Error('分享失败'));
        }
      },
    );
  });
}

/**
 * 显示WebView标题栏
 */
function showAppBar() {
  invoke({
    action: 'setTitle',
    params: {
      visible: true,
    },
  });
}

/**
 * 隐藏WebView标题栏
 */
function hideAppBar() {
  invoke({
    action: 'setTitle',
    params: {
      visible: false,
    },
  });
}

interface SetStatusBarParams {
  // 是否显示状态栏，默认为true
  visible?: boolean;
  // 状态栏文字颜色，例如 white, black
  fontColor: 'white' | 'black';
}

/**
 * 设置状态栏
 */
function setStatusBar(params: SetStatusBarParams) {
  invoke({
    action: 'setSystemStatusBar',
    params,
  });
}

export interface AppInfo {
  version: string;
  deviceId: string;
  appId: string;
  osType: 'ios' | 'android';
  channel: string;
  sysVersion: string;
}

/**
 * 获取APP信息
 */
function getAppInfo() {
  return new Promise<AppInfo>((resolve, reject) => {
    invoke(
      {
        action: 'getAppInfo',
      },
      (err, response: BridgeResponse<AppInfo>) => {
        if (err) {
          return reject(err);
        }

        const { data } = response;

        if (data) {
          resolve(data);
        } else {
          reject(new Error('获取App信息失败'));
        }
      },
    );
  });
}

interface SaveImageParams {
  // 图片base64
  picData?: string;
  // 图片网络地址
  picUrl?: string;
}

/**
 * 保存图片到手机
 */
function saveImage(params: SaveImageParams) {
  return new Promise<void>((resolve, reject) => {
    invoke(
      {
        action: 'saveImage',
        params,
      },
      (err, response: BridgeResponse<{ status: string }>) => {
        if (err) {
          return reject(err);
        }

        const { data } = response;

        if (data && data.status === 'success') {
          resolve();
        } else {
          reject(new Error('保存图片失败'));
        }
      },
    );
  });
}

/**
 * 监听APP后退
 * 该方法会阻止APP的后退
 */
function watchAppBack(callback: () => void) {
  const unwatch = invoke(
    {
      action: 'proxyBackButton',
    },
    () => {
      if (isFunction(callback)) {
        callback();
      }
    },
    true,
  );

  return () => {
    unwatch?.((finised) => {
      if (finised) {
        invoke({
          action: 'proxyBackButtonRelease',
        });
      }
    });
  };
}

/**
 * 停止监听所有APP后退
 */
function unwatchAllAppBack() {
  callbacks.delete('proxyBackButton');
  invoke({
    action: 'proxyBackButtonRelease',
  });
}

export interface Geolocation {
  longitude: number | null;
  latitude: number | null;
}

/**
 * 获取用户当前地理位置
 */
function getGeolocation() {
  return new Promise<Geolocation>((resolve, reject) => {
    invoke(
      {
        action: 'getGeolocation',
      },
      (err, response: BridgeResponse<Geolocation>) => {
        if (err) {
          return reject(err);
        }

        const { data } = response;

        if (data && isPlainObject(data)) {
          resolve(data);
        } else {
          reject(new Error('获取定位信息失败'));
        }
      },
    );
  });
}

/**
 * 发送天御验证码ticket
 * 此action会自动关闭webview
 */
function sendCaptchaTicket(ticket: string, randstr: string) {
  invoke({
    action: 'sendCaptchaTicket',
    params: {
      ticket,
      randstr,
    },
  });
}

/**
 * 获取数据
 */
function getStorage(key: string) {
  return new Promise<string>((resolve, reject) => {
    invoke(
      {
        action: 'getStorage',
        params: {
          key,
        },
      },
      (err, response: BridgeResponse) => {
        if (err) {
          return reject(err);
        }

        const { data } = response;

        if (data !== '-1') {
          resolve(data);
        } else {
          reject(new Error('获取数据失败'));
        }
      },
    );
  });
}

/**
 * 保存数据
 */
function setStorage(key: string, value: string) {
  return new Promise<string>((resolve, reject) => {
    invoke(
      {
        action: 'setStorage',
        params: {
          key,
          value,
        },
      },
      (err, response: BridgeResponse) => {
        if (err) {
          return reject(err);
        }

        const { data } = response;

        if (data !== '-1') {
          resolve(data);
        } else {
          reject(new Error('存储数据失败'));
        }
      },
    );
  });
}

/**
 * 获取数据解密
 */
function getSecretStorage(key: string) {
  return new Promise<string>((resolve, reject) => {
    invoke(
      {
        action: 'getTxxySecretStorage',
        params: {
          key,
        },
      },
      (err, response: BridgeResponse) => {
        if (err) {
          return reject(err);
        }

        const { data } = response;

        if (data !== '-1') {
          resolve(data);
        } else {
          reject(new Error('获取数据失败'));
        }
      },
    );
  });
}

/**
 * 保存数据加密
 */
function setSecretStorage(key: string, value: string) {
  return new Promise<string>((resolve, reject) => {
    invoke(
      {
        action: 'setTxxySecretStorage',
        params: {
          key,
          value,
        },
      },
      (err, response: BridgeResponse) => {
        if (err) {
          return reject(err);
        }

        const { data } = response;

        if (data !== '-1') {
          resolve(data);
        } else {
          reject(new Error('存储数据失败'));
        }
      },
    );
  });
}

/**
 * 下载天下信用报告
 */
function downloadTxxyCreditReport() {
  return getSecretStorage('txxyCreditReport');
}

/**
 * 打开外部浏览器
 */
function openBrowser(url: string) {
  invoke({
    action: 'openBrowser',
    params: {
      url,
    },
  });
}

/**
 * 拉起机构运营微信小程序
 */
function openAgencyOperationH5(params: { orderPageUrl: string; wxOriginalId: string }) {
  invoke({
    action: 'launchWeapp',
    params: {
      wechatMpId: params.wxOriginalId,
      miniProgramType:
        process.env.VUE_APP_RUNTIME_ENV !== 'prod' && process.env.VUE_APP_RUNTIME_ENV !== 'staging'
          ? WeChatMiniProgramTypes.TRIAL
          : WeChatMiniProgramTypes.RELEASE,
      wechatMpPath: params.orderPageUrl,
    },
  });
}

/**
 * 微信支付
 */
function invokeWeChatPay(orderId: string, merchant: PaymentMerchant, userCouponId: string) {
  return new Promise<void>((resolve, reject) => {
    const { token } = useUserStore();
    const curUserCouponId = userCouponId ?? null;

    let wechatMpId = null;
    switch (merchant) {
      case PaymentMerchant.YS:
        wechatMpId = YS_WECHAT_MP_ID;
        break;
      case PaymentMerchant.ZB:
        wechatMpId = ZB_WECHAT_MP_ID;
        break;
    }

    invoke(
      {
        action: 'launchWeapp',
        params: {
          wechatMpId,
          miniProgramType: WECHAT_MINI_PROGRAM_TYPE,
          wechatMpPath: `/pages/pay/index?orderId=${orderId}&merchant=${merchant}&token=${token}&userCouponId=${curUserCouponId}`,
        },
      },
      async (err, response: BridgeResponse) => {
        if (err) {
          return reject(err);
        }

        let data = { status: WeChatPaymentStatus.UNKNOWN };
        try {
          data = JSON.parse(response.data);
        } catch {}

        response.data = data;
        const { status } = data;

        if (status === WeChatPaymentStatus.SUCCESS) {
          resolve();
        } else {
          const error = new Error();
          error.name = 'BizError';
          error.response = response;

          switch (status) {
            case WeChatPaymentStatus.NOT_INSTALLED:
              error.message = '您没有安装微信，请使用支付宝支付或安装微信后再进行支付';
              break;
            // 微信支付在网络不佳或用户手动关闭支付时返回的都是取消状态，需要用户再次查询
            case WeChatPaymentStatus.CANCEL:
              error.message = '支付已取消';
              break;
            case WeChatPaymentStatus.FAIL:
              error.message = '支付失败';
              break;
            case WeChatPaymentStatus.INVALID:
              error.message = '无效的参数';
              break;
            case WeChatPaymentStatus.RESULT_UNKNOWN:
              error.message = '支付结果未知';
              break;
            default:
              error.message = '未知错误，请重试';
          }

          reject(error);
        }
      },
    );
  });
}

/**
 * 支付宝支付
 */
function invokeAlipay(params: string): Promise<void> {
  return new Promise((resolve, reject) => {
    invoke(
      {
        action: 'goAliPay',
        params: {
          payParams: params,
        },
      },
      async (err, response: BridgeResponse) => {
        if (err) {
          return reject(err);
        }

        const { status } = response.data ?? {};
        // 处理中/结果未知也可能成功，需要进一步查询
        if (status === AlipayPaymentStatus.SUCCESS) {
          resolve();
        } else {
          const error = new Error();
          error.name = 'BizError';
          error.response = response;

          switch (status) {
            case AlipayPaymentStatus.REPEAT:
              error.message = '重复请求请求';
              break;
            case AlipayPaymentStatus.CANCEL:
              error.message = '您已取消支付';
              break;
            case AlipayPaymentStatus.FAIL:
              error.message = '支付失败';
              break;
            case AlipayPaymentStatus.NETWORK_ERROR:
              error.message = '网络连接出错';
              break;
            case AlipayPaymentStatus.PROCESSING:
              error.message = '支付处理中';
              break;
            case AlipayPaymentStatus.RESULT_UNKNOWN:
              error.message = '支付结果未知';
              break;
            default:
              error.message = '未知错误，请重试';
          }

          reject(error);
        }
      },
    );
  });
}

/**
 * 支付宝独立签约
 */
function invokeAlipaySign(params: string): Promise<void> {
  return new Promise((resolve, reject) => {
    invoke(
      {
        action: 'aliAuth',
        params: {
          authParams: params,
          useBrowserLanding: true, // 是否引导下载支付宝
        },
      },
      async (err, response: BridgeResponse) => {
        if (err) {
          return reject(err);
        }

        const { status } = response.data ?? {};
        if (status === 'success') {
          resolve();
        } else {
          reject(new Error('支付宝签约调用失败'));
        }
      },
    );
  });
}

/**
 * 通知支付结果
 */
function notifyPayResult(
  productType: NotifyPaymentProductTypes,
  paymentStatus: NotifyPaymentStatus,
  productName: NotifyPaymentText,
) {
  return new Promise<void>((resolve, reject) => {
    invoke(
      {
        action: 'notifyPayResult',
        params: {
          productType,
          paymentStatus,
          productName,
        },
      },
      (err) => {
        if (err) {
          return reject(err);
        }

        resolve();
      },
    );
  });
}

/**
 * 后退到根试图
 */
function backToRootView() {
  invoke({
    action: 'backToRootView',
  });
}

/**
 * 返回到app首页
 */
function backToHomeView() {
  invoke({
    action: 'backToHomePageView',
  });
}

/**
 * 查询消息提醒开启状态
 * @returns status => '1' 已开启 status => '0' 未开启
 */
function checkNotifications(showPopup = true) {
  return new Promise<string>((resolve, reject) => {
    invoke(
      {
        action: 'checkNotifications',
        params: {
          showPopup,
        },
      },
      (err, response: BridgeResponse) => {
        if (err) {
          return reject(err);
        }

        const { data } = response;

        if (data?.status != null) {
          resolve(data);
        } else {
          reject(new Error('获取失败'));
        }
      },
    );
  });
}

interface SafeAreaInset {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

/**
 * 获取安全区距离
 */
function getSafeAreaInset() {
  return new Promise<SafeAreaInset>((resolve, reject) => {
    invoke(
      {
        action: 'getSafeAreaInset',
      },
      (err, response: BridgeResponse) => {
        if (err) {
          return reject(err);
        }

        const { data } = response;

        if (data != null) {
          resolve(data);
        } else {
          reject(new Error('获取安全区距离失败'));
        }
      },
    );
  });
}

/**
 * 显示原生安全区距离
 */
function showSafeAreaInset() {
  return new Promise<void>((resolve) => {
    invoke(
      {
        action: 'toggleSafeAreaInset',
        params: {
          visible: true,
        },
      },
      () => {
        resolve();
      },
    );
  });
}

/**
 * 隐藏原生安全区距离
 */
function hideSafeAreaInset() {
  return new Promise<void>((resolve) => {
    invoke(
      {
        action: 'toggleSafeAreaInset',
        params: {
          visible: false,
        },
      },
      () => {
        resolve();
      },
    );
  });
}

interface RequestCameraPermissionParams {
  from: 'faceVerify';
}

/**
 * 请求人脸识别权限
 * 仅Android场景下才需要单独申请权限，iOS本身就有权限
 */
function requestCameraPermission(params: RequestCameraPermissionParams) {
  return new Promise<void>((resolve, reject) => {
    if (detectEnv.android) {
      invoke(
        {
          action: 'requestCameraPermission',
          params,
        },
        (err, response: BridgeResponse) => {
          if (err) {
            return reject(err);
          }

          const { data } = response;

          if (data && data.status === 'success') {
            resolve();
          } else {
            reject(new Error('本功能需要相机权限，请完成授权后再使用本功能。'));
          }
        },
      );
    } else {
      resolve();
    }
  });
}

/**
 * 上传APP列表
 * 仅Android场景下需要
 */
function uploadAppList(origin: OriginPageTypes) {
  return new Promise<void>((resolve, reject) => {
    if (detectEnv.android) {
      invoke(
        {
          action: 'uploadAppList',
          params: {
            origin,
          },
        },
        (err) => {
          if (err) {
            return reject(err);
          }

          resolve();
        },
      );
    } else {
      resolve();
    }
  });
}

/**
 * 退出登录状态并回到首页
 */
function logout() {
  invoke({
    action: 'clearClientUserInfo',
  });
}

/**
 * 提额中心-点击去提额
 */
export type AddAmountItem = Partial<AddAmountListItem> & {
  signParams?: Record<string, any>;
  scene?: string;
};

interface GoToAddAmountResult {
  daId: number;
  process_no_continue?: number; // 2 表示用户关闭了协议弹窗
}

function goToAddAmount(addAmountItem: AddAmountItem) {
  return new Promise<GoToAddAmountResult>((resolve, reject) => {
    invoke(
      {
        action: 'goToAddAmount',
        params: {
          addAmountItem,
        },
      },
      (err, response: BridgeResponse) => {
        if (err) {
          return reject(err);
        }

        const { data } = response;

        if (data) {
          resolve(data);
        } else {
          reject(new Error('获取失败'));
        }
      },
    );
  });
}

export interface GoToLoanParams {
  diversionFundProvided?: FundProviders;
  applyStatus?: number;
  lendFailed?: number;
  creditType?: number;
  needRepay?: number;
  amountType?: number | null;
  replace?: boolean;
}

/**
 * 提额中心-点击去提现
 */
function goToLoan(params: GoToLoanParams) {
  return new Promise<void>((resolve, reject) => {
    invoke(
      {
        action: 'goToLoan',
        params,
      },
      (err) => {
        if (err) {
          return reject(err);
        }

        resolve();
      },
    );
  });
}

export interface ReservationItem {
  applyAmount: number;
  loanTerm: number;
  applyDate: string;
}
/**
 * 跳转预约借款结果页
 */
function goToReservationResult(reservationItem: ReservationItem) {
  return new Promise<void>((resolve, reject) => {
    invoke(
      {
        action: 'gotoReservationResult',
        params: {
          reservationItem,
        },
      },
      (err) => {
        if (err) {
          return reject(err);
        }

        resolve();
      },
    );
  });
}

/**
 * 请求日历权限
 */
function requestCalendarPermission() {
  return new Promise<void>((resolve, reject) => {
    invoke(
      {
        action: 'requestCalendarPermission',
      },
      (err, response: BridgeResponse) => {
        if (err) {
          return reject(err);
        }

        const { data } = response;

        if (data && data.status === 'success') {
          resolve();
        } else {
          reject(new Error('本功能需要日历权限，请完成授权后再使用。'));
        }
      },
    );
  });
}

export interface SetupEventRemindersParams {
  type: RemindersRewriteTypes;
  title: string;
  repayDateList?: string[];
  startTime?: string;
  endTime?: string;
}

/**
 * 设置事件提醒
 */
function setupEventReminders(params: SetupEventRemindersParams) {
  return new Promise<'success' | 'ignore'>((resolve, reject) => {
    invoke(
      {
        action: 'setupEventReminders',
        params,
      },
      (err, response: BridgeResponse) => {
        if (err) {
          return reject(err);
        }

        const { data } = response;

        if (data && data.status === 'success') {
          resolve('success');
        } else if (data && data.status === 'ignore') {
          resolve('ignore');
        } else {
          reject(new Error('设置事件提醒失败，请稍后重试。'));
        }
      },
    );
  });
}

/**
 * 通过WebRTC拨打电话
 */
function openWebRTCTelephone(phoneNumber: string) {
  invoke({
    action: 'openWebRTCCall',
    params: {
      phoneNumber,
    },
  });
}

export interface QuickReauditFlowParams {
  stCardId: string;
  showPreLoanPopup: string;
  showBindCardPage: string;
  showBriefPersonPage: string;
}

/**
 * 简化版授信流程
 */
function quickReauditFlow(params: QuickReauditFlowParams) {
  invoke({
    action: 'goQuickReAudit',
    params,
  });
}

/**
 * 设置当前屏幕是否允许截取/录制屏幕
 */
function setScreenSecure(enable: boolean) {
  invoke({
    action: 'setScreenSecure',
    params: {
      enable,
    },
  });
}

/**
 * 禁用滑动返回
 * 仅在iOS上生效，APP版本大于等于 6.17.0
 */
function disableSwipeBack() {
  invoke({
    action: 'swipeBack',
    params: {
      disabled: true,
    },
  });
}

/**
 * 启用滑动返回
 * 仅在iOS上生效，APP版本大于等于 6.17.0
 */
function enableSwipeBack() {
  invoke({
    action: 'swipeBack',
    params: {
      disabled: false,
    },
  });
}

function callTelConfirm(msg: string, phone: string) {
  invoke({
    action: 'callTelConfirm',
    params: {
      msg,
      phone,
    },
  });
}

export interface Contact {
  name: string;
  phone: string;
}

/**
 * 选择联系人
 */
function contactsSelect(params: { pageName: string }) {
  return new Promise<Contact>((resolve, reject) => {
    invoke(
      {
        action: 'contactsSelect',
        params,
      },
      (err, response: BridgeResponse<Contact>) => {
        if (err) {
          return reject(err);
        }

        const { data } = response;

        if (data && isPlainObject(data)) {
          resolve(data);
        } else {
          reject(new Error('获取联系人信息失败'));
        }
      },
    );
  });
}

/**
 * 显示信息安全浮层弹窗
 */
function showInfoSecurityPopup() {
  invoke({
    action: 'showInfoSecurityPopup',
  });
}

/**
 * 采集双通数据
 */
function contactsPermission() {
  invoke({
    action: 'contactsPermission',
  });
}

/**
 * 跳转到应用商店
 */
function goToAppStore() {
  return new Promise<{ success: boolean }>((resolve, reject) => {
    invoke(
      {
        action: 'goToAppStore',
      },
      (err, response: BridgeResponse<{ success: boolean }>) => {
        if (err) {
          return reject(err);
        }

        const { data } = response;

        if (data) {
          resolve(data);
        } else {
          reject(new Error('请去应用商店搜索【宜享花】，留下你的好评吧'));
        }
      },
    );
  });
}

export interface VasPackSaleParams {
  /** 搭售组件 scheme，当前仅支持月卡和报告 */
  scheme: string;
}

/**
 * 搭售原生组件（月卡 / 信用报告）
 * 验证完成或用户关闭后回调，不附带参数
 */
function vasPackSale(params: VasPackSaleParams) {
  return new Promise<void>((resolve, reject) => {
    invoke(
      {
        action: 'vasPackSale',
        params,
      },
      (err) => {
        if (err) {
          return reject(err);
        }

        resolve();
      },
    );
  });
}

interface QueryPermissionOptions {
  name: 'camera' | 'geolocation' | 'photo';
}

/**
 * 查询权限
 * @param options
 * @returns
 */
function queryPermission(options?: QueryPermissionOptions) {
  return new Promise((resolve, reject) => {
    if (
      options?.name !== 'camera' &&
      options?.name !== 'geolocation' &&
      options?.name !== 'photo'
    ) {
      reject(new Error('无效的权限名称'));
      return;
    }

    invoke(
      {
        action: 'queryPermission',
        params: {
          name: options.name,
        },
      },
      function (err, response: BridgeResponse<{ status: 'authorized' | 'denied' }>) {
        if (err) {
          return reject(err);
        }

        const { data } = response;

        if (data) {
          resolve(data);
        } else {
          reject(new Error('查询权限失败'));
        }
      },
    );
  });
}

/**
 * 打开权限设置页面
 */
function openPermissionSettings() {
  invoke({
    action: 'openPermissionSettings',
  });
}

export default {
  invoke,
  getUserInfo,
  launchLogin,
  checkLogin,
  launchFaceRecognition,
  share,
  openWebView,
  close,
  back,
  showAppBar,
  hideAppBar,
  setStatusBar,
  getAppInfo,
  saveImage,
  watchAppBack,
  unwatchAllAppBack,
  getGeolocation,
  sendCaptchaTicket,
  getStorage,
  setStorage,
  getSecretStorage,
  setSecretStorage,
  downloadTxxyCreditReport,
  openBrowser,
  openAgencyOperationH5,
  invokeWeChatPay,
  invokeAlipay,
  invokeAlipaySign,
  notifyPayResult,
  backToRootView,
  backToHomeView,
  checkNotifications,
  getSafeAreaInset,
  showSafeAreaInset,
  hideSafeAreaInset,
  requestCameraPermission,
  logout,
  goToAddAmount,
  goToLoan,
  goToReservationResult,
  requestCalendarPermission,
  setupEventReminders,
  uploadAppList,
  openWebViewWithAutofillPhoneNumber,
  checkAgreeAutofillPhoneNumber,
  saveAgreeAutofillPhoneNumber,
  openWebRTCTelephone,
  quickReauditFlow,
  setScreenSecure,
  enableSwipeBack,
  disableSwipeBack,
  callTelConfirm,
  contactsSelect,
  showInfoSecurityPopup,
  contactsPermission,
  goToAppStore,
  vasPackSale,
  queryPermission,
  openPermissionSettings,
};
